import { useEffect, useRef, useState } from 'react'

/**
 * Track whether the last scroll event was down.
 *
 * @param {Object}   [opts]
 * @param {string|HTMLElement} [opts.target]   CSS selector or element to watch (defaults to window)
 * @param {number}   [opts.threshold=0]        Minimum px before changing state
 * @returns {boolean}  true  → last motion was down
 */
export default function useScrollDirection(opts = {}) {
  const { target = window, threshold = 0 } = opts

  const [scrolledDown, setScrolledDown] = useState(false)
  const lastY = useRef(0)

  useEffect(() => {
    const el =
      target === window
        ? window
        : typeof target === 'string'
          ? document.querySelector(target)
          : target

    if (!el) return // selector didn’t match anything

    const getY = () =>
      el === window
        ? window.scrollY || document.documentElement.scrollTop
        : el.scrollTop

    const handleScroll = () => {
      const y = getY()
      const delta = y - lastY.current

      if (Math.abs(delta) > threshold) {
        setScrolledDown(delta > 0 && y > 0)
        lastY.current = y
      }
    }

    lastY.current = getY() // initial baseline
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [target, threshold])

  return scrolledDown
}
